import { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';

const ENTIDAD = 'productos';

export function useProductos() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProductos = useCallback(async () => {
    try {
      const data = await inventarioApi.getAll(ENTIDAD);
      setProductos(data);
      cache.set(ENTIDAD, data);
    } catch (e) {
      if (isOfflineError(e)) setProductos(cache.get(ENTIDAD) || []);
      else console.error('Error fetch productos', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProductos(); }, [fetchProductos]);

  const agregarProducto = async (input) => {
    const nombre = typeof input === 'string'
      ? input.trim()
      : String(input?.nombre || '').trim();
    if (!nombre) return false;

    const body = { id: uuid(), nombre };

    try {
      const created = await inventarioApi.create(ENTIDAD, body);
      const nuevo = Array.isArray(created) ? created[created.length - 1] : created;
      if (!nuevo || !nuevo.id) {
        throw new Error('Respuesta inválida al crear producto');
      }
      setProductos(prev => {
        const exists = prev.some(p => p.id === nuevo.id);
        const updated = exists ? prev : [...prev, nuevo];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return nuevo;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: body.id, nombre };
        setProductos(prev => {
          const updated = [...prev, temp];
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body });
        return temp;
      }
      throw e;
    }
  };

  const agregarProductosMasivo = async (nombres = []) => {
    const vistos = new Set(
      productos.map((p) => String(p.nombre || "").trim().toLowerCase()).filter(Boolean)
    );
    let importados = 0;
    let omitidos = 0;
    for (const raw of nombres) {
      const nombre = String(raw || "").trim();
      if (!nombre) { omitidos += 1; continue; }
      const key = nombre.toLowerCase();
      if (vistos.has(key)) { omitidos += 1; continue; }
      vistos.add(key);
      try {
        const created = await agregarProducto(nombre);
        if (created) importados += 1;
        else { omitidos += 1; vistos.delete(key); }
      } catch {
        omitidos += 1;
        vistos.delete(key);
      }
    }
    return { importados, omitidos };
  };

  return { productos, setProductos, agregarProducto, agregarProductosMasivo, loading, refetch: fetchProductos };
}
